import { IdToken } from '@auth0/auth0-react';

import { APIAuthenticatedService } from '../helpers/APICallsAuthenticated';
import { ConfigInfoService } from '../helpers/ConfigInfo';

export type AppNode =
  | 'unauthenticated'
  | 'authenticating'
  | 'initializingApi'
  | 'loadingUser'
  | 'awaitingTermsConsent'
  | 'userMissing'
  | 'ready.dashboardHome'
  | 'ready.makeReferral'
  | 'ready.makeRecommendation'
  | 'ready.forceUser'
  | 'error';

export type ReadyScreen =
  | 'dashboardHome'
  | 'makeReferral'
  | 'makeRecommendation'
  | 'forceUser';

export type GoalNode =
  | 'ready.dashboardHome'
  | 'ready.makeReferral'
  | 'ready.makeRecommendation'
  | 'ready.forceUser';

type ProtectedRouteIntentSource = 'route' | 'notification' | 'loginReturn';

export type UserIntent =
  | {
      kind: 'dashboardHome';
      route: '/dashboard';
      source: ProtectedRouteIntentSource | 'ui';
    }
  | {
      kind: 'makeReferral';
      route: '/dashboard';
      source: 'ui';
    }
  | {
      kind: 'makeRecommendation';
      route: '/dashboard';
      source: 'ui';
    }
  | {
      kind: 'forceUser';
      route: '/dashboard';
      source: 'ui';
    }
  | {
      kind: 'viewReferralsIn';
      route: '/dashboard/referrals/in';
      source: ProtectedRouteIntentSource;
    }
  | {
      kind: 'viewReferralsOut';
      route: '/dashboard/referrals/out';
      source: ProtectedRouteIntentSource;
    }
  | {
      kind: 'viewReceivedReferral';
      route: `/dashboard/referrals/in/${number}`;
      referralId: number;
      source: ProtectedRouteIntentSource;
    }
  | {
      kind: 'viewSentReferral';
      route: `/dashboard/referrals/out/${number}`;
      referralId: number;
      source: ProtectedRouteIntentSource;
    }
  | {
      kind: 'unknown';
      route: string;
      source: 'route' | 'system';
    };

export type AppRuntimeState = {
  node: AppNode;
  goalNode: GoalNode | null;
  userIntent: UserIntent;
  currentRoute: string;
  lastError: string | null;
};

export type AppCommand =
  | { type: 'BOOTSTRAP_APP' }
  | { type: 'OBSERVE_ROUTE'; route: string }
  | { type: 'OBSERVE_AUTH_STATE'; isAuthenticated: boolean; isLoading: boolean; errorMessage?: string | null }
  | { type: 'REQUEST_LOGIN'; returnTo?: string; redirectUri?: string }
  | { type: 'REQUEST_SIGNUP'; loginHint?: string; returnTo?: string; redirectUri?: string }
  | { type: 'REQUEST_LOGOUT'; returnToPath?: string }
  | { type: 'SELECT_DASHBOARD_HOME' }
  | { type: 'SELECT_MAKE_REFERRAL' }
  | { type: 'SELECT_MAKE_RECOMMENDATION' }
  | { type: 'SELECT_FORCE_USER' }
  | { type: 'SUBMIT_TERMS_ACCEPTANCE' };

export type AppEvent =
  | { type: 'ROUTE_OBSERVED'; route: string }
  | { type: 'AUTH_STATUS_OBSERVED'; isAuthenticated: boolean; isLoading: boolean; errorMessage?: string | null }
  | { type: 'LOGIN_FLOW_STARTED' }
  | { type: 'SIGNUP_FLOW_STARTED' }
  | { type: 'API_SERVICE_INIT_SUCCEEDED' }
  | { type: 'API_SERVICE_INIT_FAILED'; message: string }
  | { type: 'AUTH_USER_LOADED'; hasAgreedToTerms: boolean }
  | { type: 'AUTH_USER_NOT_FOUND' }
  | { type: 'AUTH_USER_LOAD_FAILED'; message: string }
  | { type: 'TERMS_ACCEPTANCE_SUCCEEDED' }
  | { type: 'TERMS_ACCEPTANCE_FAILED'; message: string }
  | { type: 'DASHBOARD_HOME_SELECTED' }
  | { type: 'MAKE_REFERRAL_SELECTED' }
  | { type: 'MAKE_RECOMMENDATION_SELECTED' }
  | { type: 'FORCE_USER_SELECTED' }
  | { type: 'LOGOUT_COMPLETED' }
  | { type: 'FATAL_ERROR_RAISED'; message: string };

export type RuntimeAuthService = {
  requestLogin: (options?: { returnTo?: string; redirectUri?: string }) => Promise<void>;
  requestSignup: (options?: { loginHint?: string; returnTo?: string; redirectUri?: string }) => Promise<void>;
  requestLogout: (returnToPath?: string) => Promise<void>;
  getAccessTokenSilently: () => Promise<string>;
  getIdTokenClaims: () => Promise<IdToken | undefined>;
};

type RuntimeServices = {
  auth: RuntimeAuthService;
  configInfo: ConfigInfoService;
};

type AppRuntimeDependencies = {
  createApiService?: (args: {
    getAccessTokenSilently: RuntimeAuthService['getAccessTokenSilently'];
    getIdTokenClaims: RuntimeAuthService['getIdTokenClaims'];
    configInfo: ConfigInfoService;
  }) => APIAuthenticatedService;
};

type PendingAuthRequest =
  | {
      kind: 'login';
      options?: { returnTo?: string; redirectUri?: string };
    }
  | {
      kind: 'signup';
      options?: { loginHint?: string; returnTo?: string; redirectUri?: string };
    };

type GoalPlan = {
  path: AppNode[];
  exploredRoutes: number;
};

const ALLOWED_TRANSITIONS: Record<AppNode, AppNode[]> = {
  unauthenticated: ['authenticating', 'initializingApi', 'error'],
  authenticating: ['unauthenticated', 'initializingApi', 'error'],
  initializingApi: ['unauthenticated', 'loadingUser', 'error'],
  loadingUser: ['unauthenticated', 'awaitingTermsConsent', 'userMissing', 'ready.dashboardHome', 'error'],
  awaitingTermsConsent: ['unauthenticated', 'ready.dashboardHome', 'error'],
  userMissing: ['unauthenticated', 'error'],
  'ready.dashboardHome': ['unauthenticated', 'ready.makeReferral', 'ready.makeRecommendation', 'ready.forceUser', 'error'],
  'ready.makeReferral': ['unauthenticated', 'ready.dashboardHome', 'error'],
  'ready.makeRecommendation': ['unauthenticated', 'ready.dashboardHome', 'error'],
  'ready.forceUser': ['unauthenticated', 'ready.dashboardHome', 'error'],
  error: ['unauthenticated', 'authenticating', 'initializingApi'],
};

const GOAL_GRAPH: Record<AppNode, AppNode[]> = {
  ...ALLOWED_TRANSITIONS,
};

const READY_NODE_TO_SCREEN: Record<GoalNode, ReadyScreen> = {
  'ready.dashboardHome': 'dashboardHome',
  'ready.makeReferral': 'makeReferral',
  'ready.makeRecommendation': 'makeRecommendation',
  'ready.forceUser': 'forceUser',
};

const PROTECTED_NODES = new Set<AppNode>([
  'initializingApi',
  'loadingUser',
  'awaitingTermsConsent',
  'userMissing',
  'ready.dashboardHome',
  'ready.makeReferral',
  'ready.makeRecommendation',
  'ready.forceUser',
]);

const createUnknownIntent = (route: string): UserIntent => ({
  kind: 'unknown',
  route,
  source: 'route',
});

const createDashboardIntent = (source: 'route' | 'ui' | 'loginReturn' = 'route'): UserIntent => ({
  kind: 'dashboardHome',
  route: '/dashboard',
  source,
});

const createMakeReferralIntent = (): UserIntent => ({
  kind: 'makeReferral',
  route: '/dashboard',
  source: 'ui',
});

const createMakeRecommendationIntent = (): UserIntent => ({
  kind: 'makeRecommendation',
  route: '/dashboard',
  source: 'ui',
});

const createForceUserIntent = (): UserIntent => ({
  kind: 'forceUser',
  route: '/dashboard',
  source: 'ui',
});

const createViewReferralsInIntent = (
  source: ProtectedRouteIntentSource = 'route'
): UserIntent => ({
  kind: 'viewReferralsIn',
  route: '/dashboard/referrals/in',
  source,
});

const createViewReferralsOutIntent = (
  source: ProtectedRouteIntentSource = 'route'
): UserIntent => ({
  kind: 'viewReferralsOut',
  route: '/dashboard/referrals/out',
  source,
});

const createViewReceivedReferralIntent = (
  referralId: number,
  source: ProtectedRouteIntentSource = 'route'
): UserIntent => ({
  kind: 'viewReceivedReferral',
  route: `/dashboard/referrals/in/${referralId}`,
  referralId,
  source,
});

const createViewSentReferralIntent = (
  referralId: number,
  source: ProtectedRouteIntentSource = 'route'
): UserIntent => ({
  kind: 'viewSentReferral',
  route: `/dashboard/referrals/out/${referralId}`,
  referralId,
  source,
});

const parseRouteToIntent = (route: string): UserIntent => {
  const [pathOnly] = route.split(/[?#]/);

  if (pathOnly === '/dashboard') {
    return createDashboardIntent();
  }

  if (pathOnly === '/dashboard/referrals/in') {
    return createViewReferralsInIntent();
  }

  if (pathOnly === '/dashboard/referrals/out') {
    return createViewReferralsOutIntent();
  }

  const receivedReferralMatch = pathOnly.match(/^\/dashboard\/referrals\/in\/(\d+)$/);
  if (receivedReferralMatch) {
    return createViewReceivedReferralIntent(Number(receivedReferralMatch[1]));
  }

  const sentReferralMatch = pathOnly.match(/^\/dashboard\/referrals\/out\/(\d+)$/);
  if (sentReferralMatch) {
    return createViewSentReferralIntent(Number(sentReferralMatch[1]));
  }

  return createUnknownIntent(route);
};

export const describeRouteForRuntimeLog = (route: string): string => {
  const [routeWithoutQuery] = route.split(/[?#]/);
  let pathOnly = routeWithoutQuery || '/';

  if (/^[a-z][a-z\d+.-]*:\/\//i.test(routeWithoutQuery)) {
    try {
      pathOnly = new URL(routeWithoutQuery).pathname || '/';
    } catch {
      pathOnly = routeWithoutQuery;
    }
  }

  // REQ-050: keep runtime logs useful without exposing referral ids from route paths.
  return pathOnly
    .replace(/^\/dashboard\/referrals\/(in|out)\/\d+$/, '/dashboard/referrals/$1/:referralId')
    .replace(/^\/admins\/referrals\/\d+\/(as-referrer|as-offerer)$/, '/admins/referrals/:referralId/$1');
};

const getGoalNodeForIntent = (intent: UserIntent): GoalNode | null => {
  switch (intent.kind) {
    case 'dashboardHome':
    case 'viewReferralsIn':
    case 'viewReferralsOut':
    case 'viewReceivedReferral':
    case 'viewSentReferral':
      return 'ready.dashboardHome';
    case 'makeReferral':
      return 'ready.makeReferral';
    case 'makeRecommendation':
      return 'ready.makeRecommendation';
    case 'forceUser':
      return 'ready.forceUser';
    case 'unknown':
    default:
      return null;
  }
};

export const createInitialState = (): AppRuntimeState => ({
  node: 'unauthenticated',
  goalNode: null,
  userIntent: createUnknownIntent('/'),
  currentRoute: '/',
  lastError: null,
});

const isRefreshTokenError = (message: string, error: unknown) =>
  message.startsWith('Missing Refresh Token') ||
  (typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: string }).code === 'missing_refresh_token') ||
  message.startsWith('Unknown or invalid refresh token');

const isTransitionAllowed = (from: AppNode, to: AppNode) =>
  from === to || ALLOWED_TRANSITIONS[from].includes(to);

const moveToNode = (
  state: AppRuntimeState,
  nextNode: AppNode,
  extras: Partial<AppRuntimeState> = {}
): AppRuntimeState => {
  if (!isTransitionAllowed(state.node, nextNode)) {
    throw new Error(`Illegal state transition: ${state.node} -> ${nextNode}`);
  }

  return {
    ...state,
    ...extras,
    node: nextNode,
  };
};

export const findShortestGoalPath = (start: AppNode, goal: GoalNode): GoalPlan | null => {
  const queue: AppNode[][] = [[start]];
  const visited = new Set<AppNode>([start]);
  let exploredRoutes = 0;

  while (queue.length > 0) {
    const path = queue.shift()!;
    const currentNode = path[path.length - 1];
    exploredRoutes += 1;

    if (currentNode === goal) {
      return { path, exploredRoutes };
    }

    for (const nextNode of GOAL_GRAPH[currentNode]) {
      if (visited.has(nextNode)) {
        continue;
      }

      visited.add(nextNode);
      queue.push([...path, nextNode]);
    }
  }

  return { path: [], exploredRoutes };
};

const goalReached = (state: AppRuntimeState) =>
  state.goalNode !== null && state.node === state.goalNode;

export const isAuthenticatedNode = (node: AppNode) => PROTECTED_NODES.has(node);

export const isBusyNode = (node: AppNode) =>
  node === 'authenticating' || node === 'initializingApi' || node === 'loadingUser';

export const getReadyScreen = (node: AppNode): ReadyScreen | null => {
  if (node in READY_NODE_TO_SCREEN) {
    return READY_NODE_TO_SCREEN[node as GoalNode];
  }

  return null;
};

export const appReducer = (state: AppRuntimeState, event: AppEvent): AppRuntimeState => {
  switch (event.type) {
    case 'ROUTE_OBSERVED':
      return {
        ...state,
        currentRoute: event.route,
        userIntent: parseRouteToIntent(event.route),
      };

    /*
     * Central access policy:
     * 1. Observe auth state first.
     * 2. Initialize the authenticated API client.
     * 3. Load the authenticated user record.
     * 4. Gate protected routes on T&Cs.
     * 5. Only then expose the protected dashboard shell.
     *
     * Route intent is tracked separately from readiness. Referral detail routes
     * still map to `ready.dashboardHome` because the ready node means the
     * protected shell is available; React Router renders the specific route.
     */
    case 'AUTH_STATUS_OBSERVED':
      if (event.errorMessage) {
        return moveToNode(state, 'error', { lastError: event.errorMessage });
      }

      if (event.isLoading) {
        if (state.node === 'authenticating') {
          return state;
        }

        if (state.node === 'unauthenticated' || state.node === 'error') {
          return moveToNode(state, 'authenticating', { lastError: null });
        }

        return {
          ...state,
          lastError: null,
        };
      }

      if (!event.isAuthenticated) {
        return moveToNode(state, 'unauthenticated', {
          goalNode: null,
          lastError: null,
        });
      }

      if (state.node === 'initializingApi' || state.node === 'loadingUser' || isAuthenticatedNode(state.node)) {
        return state;
      }

      return moveToNode(state, 'initializingApi', { lastError: null });

    case 'LOGIN_FLOW_STARTED':
    case 'SIGNUP_FLOW_STARTED':
      return moveToNode(state, 'authenticating', { lastError: null });

    case 'API_SERVICE_INIT_SUCCEEDED':
      return moveToNode(state, 'loadingUser', { lastError: null });

    case 'API_SERVICE_INIT_FAILED':
      return moveToNode(state, 'error', { lastError: event.message });

    case 'AUTH_USER_LOADED':
      return moveToNode(
        state,
        event.hasAgreedToTerms ? 'ready.dashboardHome' : 'awaitingTermsConsent',
        { lastError: null }
      );

    case 'AUTH_USER_NOT_FOUND':
      return moveToNode(state, 'userMissing', { lastError: null });

    case 'AUTH_USER_LOAD_FAILED':
      return moveToNode(state, 'error', { lastError: event.message });

    case 'TERMS_ACCEPTANCE_SUCCEEDED':
      return moveToNode(state, 'ready.dashboardHome', { lastError: null });

    case 'TERMS_ACCEPTANCE_FAILED':
      return {
        ...state,
        lastError: event.message,
      };

    case 'DASHBOARD_HOME_SELECTED':
      return moveToNode(state, 'ready.dashboardHome', {
        userIntent: createDashboardIntent('ui'),
        lastError: null,
      });

    case 'MAKE_REFERRAL_SELECTED':
      return moveToNode(state, 'ready.makeReferral', {
        userIntent: createMakeReferralIntent(),
        lastError: null,
      });

    case 'MAKE_RECOMMENDATION_SELECTED':
      return moveToNode(state, 'ready.makeRecommendation', {
        userIntent: createMakeRecommendationIntent(),
        lastError: null,
      });

    case 'FORCE_USER_SELECTED':
      return moveToNode(state, 'ready.forceUser', {
        userIntent: createForceUserIntent(),
        lastError: null,
      });

    case 'LOGOUT_COMPLETED':
      return moveToNode(state, 'unauthenticated', {
        goalNode: null,
        lastError: null,
      });

    case 'FATAL_ERROR_RAISED':
      return moveToNode(state, 'error', { lastError: event.message });

    default:
      return state;
  }
};

export class AppRuntime {
  private readonly dependencies: AppRuntimeDependencies;
  private state: AppRuntimeState = createInitialState();
  private readonly subscribers = new Set<() => void>();
  private services: Partial<RuntimeServices> = {};
  private apiService: APIAuthenticatedService | null = null;
  private apiInitPromise: Promise<void> | null = null;
  private authUserLoadPromise: Promise<void> | null = null;
  private termsAcceptancePromise: Promise<void> | null = null;
  private pendingAuthRequest: PendingAuthRequest | null = null;

  constructor(dependencies: AppRuntimeDependencies = {}) {
    this.dependencies = dependencies;
  }

  subscribe = (listener: () => void) => {
    this.subscribers.add(listener);
    return () => {
      this.subscribers.delete(listener);
    };
  };

  getState = () => this.state;

  getApiService = () => this.apiService;

  configureServices = (services: Partial<RuntimeServices>) => {
    this.services = {
      ...this.services,
      ...services,
    };
  };

  dispatchCommand = async (command: AppCommand): Promise<void> => {
    this.logCommand(command);

    switch (command.type) {
      case 'BOOTSTRAP_APP':
        return;

      case 'OBSERVE_ROUTE':
        this.applyEvent({ type: 'ROUTE_OBSERVED', route: command.route });
        return;

      case 'OBSERVE_AUTH_STATE':
        this.applyEvent({
          type: 'AUTH_STATUS_OBSERVED',
          isAuthenticated: command.isAuthenticated,
          isLoading: command.isLoading,
          errorMessage: command.errorMessage,
        });
        return;

      case 'REQUEST_LOGIN':
        this.pendingAuthRequest = {
          kind: 'login',
          options: {
            returnTo: command.returnTo,
            redirectUri: command.redirectUri,
          },
        };
        this.updateGoal(getGoalNodeForIntent(this.state.userIntent) ?? 'ready.dashboardHome');
        await this.startAuthFlow('LOGIN_FLOW_STARTED');
        return;

      case 'REQUEST_SIGNUP':
        this.pendingAuthRequest = {
          kind: 'signup',
          options: {
            loginHint: command.loginHint,
            returnTo: command.returnTo,
            redirectUri: command.redirectUri,
          },
        };
        this.updateGoal(getGoalNodeForIntent(this.state.userIntent) ?? 'ready.dashboardHome');
        await this.startAuthFlow('SIGNUP_FLOW_STARTED');
        return;

      case 'REQUEST_LOGOUT':
        await this.requireAuthService().requestLogout(command.returnToPath);
        return;

      case 'SELECT_DASHBOARD_HOME':
        await this.setIntentGoalAndAdvance(createDashboardIntent('ui'));
        return;

      case 'SELECT_MAKE_REFERRAL':
        await this.setIntentGoalAndAdvance(createMakeReferralIntent());
        return;

      case 'SELECT_MAKE_RECOMMENDATION':
        await this.setIntentGoalAndAdvance(createMakeRecommendationIntent());
        return;

      case 'SELECT_FORCE_USER':
        await this.setIntentGoalAndAdvance(createForceUserIntent());
        return;

      case 'SUBMIT_TERMS_ACCEPTANCE':
        await this.submitTermsAcceptance();
        return;

      default:
        return;
    }
  };

  private requireAuthService = () => {
    if (!this.services.auth) {
      throw new Error('App runtime auth service has not been configured');
    }

    return this.services.auth;
  };

  private requireConfigInfo = () => {
    if (!this.services.configInfo) {
      throw new Error('App runtime config service has not been configured');
    }

    return this.services.configInfo;
  };

  private notify = () => {
    this.subscribers.forEach((listener) => listener());
  };

  private applyEvent = (event: AppEvent) => {
    const previousState = this.state;
    let nextState = appReducer(previousState, event);

    if (goalReached(nextState)) {
      nextState = {
        ...nextState,
        goalNode: null,
      };
    }

    this.state = nextState;
    this.logEvent(previousState, event, nextState);
    this.notify();

    if (previousState.node !== nextState.node) {
      this.runStateEntryActions(nextState.node);
    }

    if (nextState.goalNode !== null && nextState.goalNode !== nextState.node) {
      void this.advanceTowardsGoal();
    }
  };

  private updateGoal = (goalNode: GoalNode) => {
    this.state = {
      ...this.state,
      goalNode,
    };
    console.log(
      `[app-runtime] goal=${goalNode} node=${this.state.node} route=${describeRouteForRuntimeLog(this.state.currentRoute)}`
    );
    this.notify();
  };

  private setIntentGoalAndAdvance = async (intent: UserIntent) => {
    this.state = {
      ...this.state,
      userIntent: intent,
      goalNode: getGoalNodeForIntent(intent),
    };
    this.notify();
    await this.advanceTowardsGoal();
  };

  private logCommand = (command: AppCommand) => {
    console.log(
      `[app-runtime] command=${command.type} node=${this.state.node} goal=${this.state.goalNode ?? 'none'} intent=${this.state.userIntent.kind} route=${describeRouteForRuntimeLog(this.state.currentRoute)}`
    );
  };

  private logEvent = (
    previousState: AppRuntimeState,
    event: AppEvent,
    nextState: AppRuntimeState
  ) => {
    console.log(
      `[app-runtime] event=${event.type} prev=${previousState.node} next=${nextState.node} goal=${nextState.goalNode ?? 'none'} intent=${nextState.userIntent.kind} route=${describeRouteForRuntimeLog(nextState.currentRoute)}`
    );
  };

  private logGoalPlan = (goalNode: GoalNode, plan: GoalPlan | null) => {
    if (!plan || plan.path.length === 0) {
      console.log(
        `[app-runtime] planner goal=${goalNode} explored=${plan?.exploredRoutes ?? 0} path=(none)`
      );
      return;
    }

    console.log(
      `[app-runtime] planner goal=${goalNode} explored=${plan.exploredRoutes} path=${plan.path.join(' -> ')}`
    );
  };

  private resetSessionState = () => {
    this.apiService = null;
    this.apiInitPromise = null;
    this.authUserLoadPromise = null;
    this.termsAcceptancePromise = null;
    this.pendingAuthRequest = null;
  };

  private runStateEntryActions = (node: AppNode) => {
    switch (node) {
      case 'unauthenticated':
        this.resetSessionState();
        return;
      case 'initializingApi':
        void this.ensureApiServiceReady();
        return;
      case 'loadingUser':
        void this.ensureAuthenticatedUserLoaded();
        return;
      default:
        return;
    }
  };

  private advanceTowardsGoal = async () => {
    if (!this.state.goalNode) {
      return;
    }

    if (this.state.goalNode === this.state.node) {
      this.state = {
        ...this.state,
        goalNode: null,
      };
      this.notify();
      return;
    }

    const plan = findShortestGoalPath(this.state.node, this.state.goalNode);
    this.logGoalPlan(this.state.goalNode, plan);

    if (!plan || plan.path.length < 2) {
      return;
    }

    const [currentNode, nextNode] = plan.path;
    const edge = `${currentNode}->${nextNode}`;

    switch (edge) {
      case 'ready.makeReferral->ready.dashboardHome':
      case 'ready.makeRecommendation->ready.dashboardHome':
      case 'ready.forceUser->ready.dashboardHome':
        this.applyEvent({ type: 'DASHBOARD_HOME_SELECTED' });
        return;

      case 'ready.dashboardHome->ready.makeReferral':
        this.applyEvent({ type: 'MAKE_REFERRAL_SELECTED' });
        return;

      case 'ready.dashboardHome->ready.makeRecommendation':
        this.applyEvent({ type: 'MAKE_RECOMMENDATION_SELECTED' });
        return;

      case 'ready.dashboardHome->ready.forceUser':
        this.applyEvent({ type: 'FORCE_USER_SELECTED' });
        return;

      case 'awaitingTermsConsent->ready.dashboardHome':
        console.log('[app-runtime] planner waiting for user action: SUBMIT_TERMS_ACCEPTANCE');
        return;

      case 'authenticating->initializingApi':
      case 'initializingApi->loadingUser':
      case 'loadingUser->awaitingTermsConsent':
      case 'loadingUser->ready.dashboardHome':
      case 'loadingUser->userMissing':
        console.log('[app-runtime] planner waiting for external event to advance');
        return;

      default:
        return;
    }
  };

  private startAuthFlow = async (eventType: 'LOGIN_FLOW_STARTED' | 'SIGNUP_FLOW_STARTED') => {
    this.applyEvent({ type: eventType });

    const auth = this.requireAuthService();
    const pendingRequest = this.pendingAuthRequest;

    if (!pendingRequest) {
      return;
    }

    if (pendingRequest.kind === 'login') {
      await auth.requestLogin(pendingRequest.options);
      return;
    }

    await auth.requestSignup(pendingRequest.options);
  };

  private ensureApiServiceReady = async () => {
    if (this.apiService) {
      this.applyEvent({ type: 'API_SERVICE_INIT_SUCCEEDED' });
      return;
    }

    if (this.apiInitPromise) {
      await this.apiInitPromise;
      return;
    }

    const auth = this.requireAuthService();
    const configInfo = this.requireConfigInfo();

    this.apiInitPromise = (async () => {
      try {
        const nextApiService = this.dependencies.createApiService
          ? this.dependencies.createApiService({
              getAccessTokenSilently: () => auth.getAccessTokenSilently(),
              getIdTokenClaims: auth.getIdTokenClaims,
              configInfo,
            })
          : APIAuthenticatedService.getInstance(
              () => auth.getAccessTokenSilently(),
              auth.getIdTokenClaims,
              configInfo
            );

        await nextApiService.init();
        await nextApiService.getApiInfo();

        this.apiService = nextApiService;
        this.applyEvent({ type: 'API_SERVICE_INIT_SUCCEEDED' });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';

        if (isRefreshTokenError(message, error)) {
          await auth.requestLogout();
          return;
        }

        this.apiService = null;
        this.applyEvent({ type: 'API_SERVICE_INIT_FAILED', message });
      } finally {
        this.apiInitPromise = null;
      }
    })();

    await this.apiInitPromise;
  };

  private ensureAuthenticatedUserLoaded = async () => {
    if (!this.apiService) {
      return;
    }

    if (this.authUserLoadPromise) {
      await this.authUserLoadPromise;
      return;
    }

    this.authUserLoadPromise = (async () => {
      try {
        const user = await this.apiService!.getAuthenticatedUser();

        if (user === null) {
          this.applyEvent({ type: 'AUTH_USER_NOT_FOUND' });
          return;
        }

        this.pendingAuthRequest = null;
        this.applyEvent({
          type: 'AUTH_USER_LOADED',
          hasAgreedToTerms: Boolean(user.hasAgreedTsAndCs),
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Failed to load authenticated user';

        this.applyEvent({ type: 'AUTH_USER_LOAD_FAILED', message });
      } finally {
        this.authUserLoadPromise = null;
      }
    })();

    await this.authUserLoadPromise;
  };

  private submitTermsAcceptance = async () => {
    if (this.state.node !== 'awaitingTermsConsent') {
      console.log(`[app-runtime] SUBMIT_TERMS_ACCEPTANCE ignored in node=${this.state.node}`);
      return;
    }

    if (!this.apiService) {
      this.applyEvent({
        type: 'TERMS_ACCEPTANCE_FAILED',
        message: 'API service is not ready',
      });
      return;
    }

    if (this.termsAcceptancePromise) {
      await this.termsAcceptancePromise;
      return;
    }

    this.termsAcceptancePromise = (async () => {
      try {
        const user = await this.apiService!.updateUserAgreementStatus();

        if (!user || !user.hasAgreedTsAndCs) {
          this.applyEvent({
            type: 'TERMS_ACCEPTANCE_FAILED',
            message: 'Terms acceptance did not return a consented user',
          });
          return;
        }

        this.applyEvent({ type: 'TERMS_ACCEPTANCE_SUCCEEDED' });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Failed to update terms acceptance';

        this.applyEvent({ type: 'TERMS_ACCEPTANCE_FAILED', message });
      } finally {
        this.termsAcceptancePromise = null;
      }
    })();

    await this.termsAcceptancePromise;
  };
}

const appRuntimeSingleton = new AppRuntime();

export const getAppRuntime = () => appRuntimeSingleton;
